import { Contact, LocationData } from '@/apis/types';
import theme from '@/assets/theme';
import { Col, Flex, message, Row, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// redux
import type { ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { UnknownAction } from 'redux';
import { setShowUnknownLocations } from '../../redux-toolkit/slice/SettingsLocationSlice.js';
import {
  GetLocationsWithPaginationAndSearch,
  GetSingleLocation,
} from '../../apis/onboarding.js';
// icons
import { DeleteLocation } from '../../apis/settings.js';
import mailIcon from '../../assets/images/mail.svg';
import phoneIcon from '../../assets/images/phone.svg';
// components
import {
  CustomPagination,
  CustomParagraph,
  PageHeaderActions,
  TeamBadge,
} from '../../components';
import CustomDeleteModal from '../Modals/CustomDeleteModal.jsx';
import CommonDrawer from '../../components/CommonDrawer';
import LocationForm from './LocationForm.jsx';
// helpers
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import useUnauthorize from '../../hooks/useUnauthorize';
import { useMenuPermissions } from '@/hooks';
import { MenuLabels, SubMenuLabels } from '@/common/enum';
import { DeleteIcon, GeoFenceIcon, PencilIcon } from '@/assets/icons/icons.js';
import { useTableScroll } from '@/hooks/useTableScroll';
import styles from './Locations.module.scss';
import {
  effectiveOperatingHours,
  formatRanges,
  groupWeekly,
} from './OperatingHours/operatingHours';

interface SettingsLocationsProps {
  title: string;
}

interface LRootState {
  settingsLocations: {
    selectedLocationId: string | null;
    showUnknownLocations: boolean;
  };
}

/**
 * Settings page for managing locations with pagination, search, and CRUD operations
 *
 * @param title - Page title to display in the header
 */
const SettingsLocations: React.FC<SettingsLocationsProps> = ({ title }) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const dispatch =
    useDispatch<ThunkDispatch<LRootState, unknown, UnknownAction>>();
  const tableScroll = useTableScroll();
  const { t } = useTranslation('settings');
  const settingsLocations = useSelector(
    (state: LRootState) => state.settingsLocations
  );
  const LocationPermissions = useMenuPermissions(
    MenuLabels.Settings,
    SubMenuLabels.Locations
  );
  const toggle = settingsLocations.showUnknownLocations;
  const [locationsList, setLocationsList] = useState<LocationData[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  // The drawer's "Edit operating hours" view; reset whenever the drawer closes.
  const [hoursEditorOpen, setHoursEditorOpen] = useState<boolean>(false);
  // Back is ignored while the hours save, so a failed save keeps the draft.
  const [hoursSaving, setHoursSaving] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(
    null
  );

  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    null
  );
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [maxPageSize, setMaxPageSize] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const openDrawer = queryParams.get('openDrawer') === 'true';
  const locationID = queryParams.get('id');
  const toggleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.checked;
    dispatch(setShowUnknownLocations(val));
    setIsChecked(val);
    setPage(1);
  };

  const handleSearchChange = (value?: string) => {
    // The API skips an empty search exactly as it skips undefined.
    setSearch(value ?? '');
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };

  const refreshLocations = () => {
    fetchLocations(isChecked, page, search);
  };

  const fetchLocations = useCallback(
    (checked: boolean, page: number, search: string) => {
      showSpin();
      GetLocationsWithPaginationAndSearch(token ?? '', checked, page, search)
        .then((res) => {
          const data = res?.data?.data;
          if (res?.data?.data?.is_success) {
            setLocationsList(data?.data?.data || []);
            setTotalPages(data?.page_count ?? 1);
            setMaxPageSize(data?.total_count ?? 0);
          } else {
            message.error(data?.message || 'Failed to fetch locations');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch locations');
        })
        .finally(() => {
          hideSpin();
        });
    },
    [hideSpin, showSpin, token, unauthorize]
  );

  useEffect(() => {
    fetchLocations(toggle, page, search);
  }, [toggle, page, search, fetchLocations]);

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);
  const handleDeleteClick = (location: LocationData) => {
    setSelectedLocation(location);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedLocation) return;
    showSpin();
    DeleteLocation(token ?? '', selectedLocation.id)
      .then((res) => {
        if (res?.data?.data?.is_success) {
          message.success(res.data.data.message);
          fetchLocations(isChecked, page, search);
        } else {
          message.error(res.data.data.message);
        }
      })
      .catch(() => message.error('Failed to delete location'))
      .finally(() => {
        hideSpin();
        setDeleteModalVisible(false);
        setSelectedLocation(null);
      });
  };

  const fetchSingleLocation = useCallback(
    (id: string) => {
      showSpin();
      GetSingleLocation(token ?? '', id)
        .then((res) => {
          const data = res?.data?.data;
          if (res?.data?.data?.is_success) {
            setCurrentLocation(data?.data || null);
          } else {
            message.error(data?.message || 'Failed to fetch location');
          }
        })
        .catch((error) => {
          unauthorize(error);
          message.error('Failed to fetch location');
        })
        .finally(() => {
          hideSpin();
        });
    },
    [hideSpin, showSpin, token, unauthorize]
  );

  const handleEdit = useCallback(
    (id: string) => {
      fetchSingleLocation(id);
      setDrawerOpen(true);
    },
    [fetchSingleLocation, setDrawerOpen]
  );

  useEffect(() => {
    if (!drawerOpen) setHoursEditorOpen(false);
  }, [drawerOpen]);

  const handleAddLocation = () => {
    setCurrentLocation(null);
    setDrawerOpen(true);
  };

  const formatOperatingHours = (record: LocationData): string => {
    // Same source as the Edit location card, legacy range included.
    const hours = effectiveOperatingHours(
      record.operating_hours,
      record.receiving_hours,
      record.timezone_str
    );
    if (!hours) return '--';
    const open = groupWeekly(hours.weekly).filter((g) => g.ranges.length);
    return open.length
      ? open.map((g) => `${g.label}: ${formatRanges(g.ranges)}`).join('\n')
      : 'Closed';
  };

  useEffect(() => {
    if (openDrawer && locationID) {
      if (locationID) {
        setIsChecked(toggle);
        handleEdit(locationID);
      }
      const cleanedPath = location.pathname;
      Promise.resolve(navigate(cleanedPath, { replace: true })).catch(
        console.error
      );
    }
  }, [
    toggle,
    openDrawer,
    locationID,
    locationsList,
    handleEdit,
    location.pathname,
    navigate,
  ]);

  const isLargeScreen = window.innerWidth >= 1690;

  const columns: ColumnsType<LocationData> = [
    {
      title: t('locationName'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Flex gap={12} align='center'>
          <CustomParagraph
            text={text || '--'}
            fontSize='14px'
            fontWeight={600}
            color='var(--text-primary)'
          />
          {Array.isArray(record?.polygon) && record.polygon.length >= 3 && (
            <Tooltip title={t('geoFenceEnabled')}>
              <span
                className={styles.geofenceIcon}

                aria-label={t('geoFenceEnabled')}
              >
                <GeoFenceIcon color='var(--text-primary)' />
              </span>
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: t('address'),
      dataIndex: 'address',
      key: 'address',
      width: isLargeScreen ? undefined : '250',
      render: (text) => (
        <CustomParagraph
          text={text || '--'}
          fontSize='14px'
          fontWeight={400}
          color='var(--text-primary)'
        />
      ),
    },
    {
      title: t('primaryContact'),
      dataIndex: 'contacts',
      key: 'contact',
      render: (contacts) => {
        const primaryContact =
          contacts?.find((c: Contact) => c.is_primary_contact) || {};

        return (
          <Flex gap={8} align='center'>
            <CustomParagraph
              text={primaryContact.name || '--'}
              fontSize='14px'
              fontWeight={400}
              color='var(--text-primary)'
            />
            <div className={styles.contactLine}>
              <Tooltip title={primaryContact.phone_number || '--'}>
                <img className={styles.phoneIcon} src={phoneIcon} alt='Phone' />
              </Tooltip>
              <Tooltip title={primaryContact.email || '--'}>
                <img className={styles.mailIcon} src={mailIcon} alt='Mail' />
              </Tooltip>
            </div>
          </Flex>
        );
      },
    },
    {
      title: t('operatingHours'),
      dataIndex: 'receiving_hours',
      key: 'hours',
      render: (_, record) => (
        <div className={styles.hoursCell}>
          <CustomParagraph
            text={formatOperatingHours(record)}
            fontSize='14px'
            fontWeight={400}
            color='var(--text-primary)'
          />
        </div>
      ),
    },
    {
      title: `${theme.themeType === 2 ? t('clientsWithAccess') : t('teamsWithAccess')}`,
      dataIndex: 'teams',
      key: 'teams',
      render: (_, record) => {
        return (
          <div>
            {record?.teams?.length > 0 ? (
              <div className='flex gap-x-2 white-space-nowrap'>
                <TeamBadge
                  color={record?.teams[0]?.color}
                  text={record?.teams[0]?.name}
                  tooltipText={record?.teams[0]?.name}
                />
                {record?.teams.length > 1 && (
                  <Tooltip
                    title={record.teams
                      .slice(1)
                      .map((team) => team.name)
                      .join(', ')}
                    placement='top'
                  >
                    <div className={styles.teamsMore}>
                      <TeamBadge text={`+ ${record?.teams.length - 1}`} />
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (
              <>
                {' '}
                <div>--</div>{' '}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Flex align='center' gap={6}>
          {record?.permissions?.is_update && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              onClick={() => handleEdit(record?.id)}
              role='img'
              aria-label='Edit'
            >
              <PencilIcon color='var(--text-primary)' />
            </Flex>
          )}
          {record?.permissions?.is_delete && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              onClick={() => handleDeleteClick(record)}
              role='img'
              aria-label='Delete'
            >
              <DeleteIcon color='var(--text-primary)' />
            </Flex>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          toggleTooltip={t('unknownLocationsTooltip')}
          showToggle={true}
          toggleText={t('showUnknownLocations')}
          title={`${title}`}
          totalCount={maxPageSize}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          toggleChecked={toggle}
          onToggleChange={toggleSwitch}
          addButtonText={t('add')}
          onSearchChange={handleSearchChange}
          onSearchClear={handleSearchChange}
          showAddButton={LocationPermissions?.is_create}
          onAddClick={handleAddLocation}
        />

        <Table
          columns={columns}
          dataSource={locationsList}
          pagination={false}
          className='settings-table settings-table-action-cols'
          scroll={tableScroll}
          tableLayout='fixed'
        />
        {locationsList.length > 0 && (
          <Row style={{ width: '100%', marginTop: '12px' }} justify='center'>
            <Col>
              <CustomPagination
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </Col>
          </Row>
        )}
      </div>

      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title={t('removeLocation')}
        message={t('removeLocationMessage')}
        cancelText={t('cancel')}
        confirmText={t('delete')}
        confirmColor='var(--danger)'
      />
      {/* <LocationAutoEditLoader
        token={token}
        onEdit={handleEdit}
        isDrawerOpen={drawerOpen}
      /> */}

      <CommonDrawer
        open={drawerOpen}
        onClose={() => {
          // Closing mid-save would drop the hours draft if the save fails.
          if (!hoursSaving) setDrawerOpen(false);
        }}
        // In the hours editor, Escape or a stray click outside would
        // discard the draft; the back arrow, Cancel and the X still work.
        keyboard={!hoursEditorOpen}
        maskClosable={!hoursEditorOpen}
        onBack={
          hoursEditorOpen
            ? () => {
                if (!hoursSaving) setHoursEditorOpen(false);
              }
            : undefined
        }
        backLabel={currentLocation ? 'Edit location' : 'Add location'}
      >
        <LocationForm
          unauthorize={unauthorize}
          locationData={currentLocation}
          drawerOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          refreshLocations={refreshLocations}
          LocationPermissions={LocationPermissions}
          hoursEditorOpen={hoursEditorOpen}
          onHoursEditorOpenChange={setHoursEditorOpen}
          onHoursSavingChange={setHoursSaving}
        />
      </CommonDrawer>
    </>
  );
};

export default SettingsLocations;
